(() => {
  if (customElements.get('fh-cookie-bar')) return;

  const STORAGE_KEY = 'fh_cookie_consent';

  class FhCookieBar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.fh-cookie__bar');
      this.toggles = Array.from(this.querySelectorAll('[data-fh-cookie-consent]'));
      this.customizeButton = this.querySelector('[data-fh-cookie-action="customize"]');

      this.addEventListener('click', this.handleClick);

      if (this.dataset.designMode === 'true') {
        this.reveal();
        document.addEventListener('shopify:section:select', this.handleEditorSelect);
        return;
      }

      this.loadPrivacyApi()
        .then((api) => {
          this.api = api;
          if (this.shouldShow()) this.reveal();
        })
        .catch(() => {
          if (!this.storedConsent()) this.reveal();
        });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:section:select', this.handleEditorSelect);
    }

    /* Dans l'éditeur de thème, re-afficher la barre quand la section est sélectionnée. */
    handleEditorSelect = (event) => {
      if (event.target.contains(this)) this.reveal();
    };

    /* L'API Customer Privacy est chargée à la demande par Shopify. */
    loadPrivacyApi() {
      return new Promise((resolve, reject) => {
        if (window.Shopify?.customerPrivacy) {
          resolve(window.Shopify.customerPrivacy);
          return;
        }

        if (typeof window.Shopify?.loadFeatures !== 'function') {
          reject(new Error('Shopify.loadFeatures unavailable'));
          return;
        }

        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            if (error || !window.Shopify.customerPrivacy) {
              reject(error || new Error('customerPrivacy unavailable'));
              return;
            }
            resolve(window.Shopify.customerPrivacy);
          }
        );
      });
    }

    storedConsent() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return null;
      }
    }

    rememberConsent() {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch (error) {
        /* mode navigation privée : on ignore */
      }
    }

    shouldShow() {
      try {
        if (typeof this.api?.shouldShowBanner === 'function') {
          return this.api.shouldShowBanner();
        }
      } catch (error) {
        /* on retombe sur le stockage local */
      }
      return !this.storedConsent();
    }

    reveal() {
      this.hidden = false;
      requestAnimationFrame(() => {
        this.dataset.state = 'visible';
      });
    }

    dismiss() {
      this.dataset.state = 'hidden';
      this.dataset.prefs = 'closed';
      window.setTimeout(() => {
        this.hidden = true;
      }, 350);
    }

    handleClick = (event) => {
      const trigger = event.target.closest('[data-fh-cookie-action]');
      if (!trigger || !this.contains(trigger)) return;

      switch (trigger.dataset.fhCookieAction) {
        case 'accept':
          this.setConsent({ analytics: true, marketing: true, preferences: true });
          break;
        case 'decline':
          this.setConsent({ analytics: false, marketing: false, preferences: false });
          break;
        case 'customize':
          this.togglePreferences();
          break;
        case 'save':
          this.setConsent({
            analytics: this.isChecked('analytics'),
            marketing: this.isChecked('marketing'),
            preferences: this.isChecked('preferences'),
          });
          break;
      }
    };

    isChecked(category) {
      return Boolean(this.toggles.find((input) => input.dataset.fhCookieConsent === category)?.checked);
    }

    togglePreferences() {
      const open = this.dataset.prefs !== 'open';
      this.dataset.prefs = open ? 'open' : 'closed';
      this.customizeButton?.setAttribute('aria-expanded', String(open));
    }

    setConsent({ analytics, marketing, preferences }) {
      this.rememberConsent();

      if (this.dataset.designMode === 'true') {
        this.dismiss();
        return;
      }

      try {
        this.api?.setTrackingConsent(
          {
            analytics,
            marketing,
            preferences,
            sale_of_data: marketing,
          },
          () => {}
        );
      } catch (error) {
        /* le choix reste mémorisé localement */
      }

      this.dismiss();
    }
  }

  customElements.define('fh-cookie-bar', FhCookieBar);
})();
